import * as React from 'react';
import { Card, CardContent } from '@shared/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@shared/components/ui/tabs';
import { cn } from '@shared/lib/utils';
import { PANEL_SPACING } from '../../constants/spacing';
import { useSectionTitleClassName } from '../../context/SectionStyleContext';

export interface TabItem {
    /** Unique identifier for the tab */
    value: string;
    /** Display label for the tab trigger */
    label: string;
    /** Content to render when tab is active */
    content: React.ReactNode;
    /** Optional actions to display on the right of tabs when this tab is active */
    tabActions?: React.ReactNode;
}

interface TabbedSectionProps {
    /** Section title displayed above tabs */
    title: string;
    /** Array of tab configurations */
    tabs: TabItem[];
    /** Default active tab value */
    defaultTab?: string;
    /** Additional CSS classes for the card */
    className?: string;
    /** Optional callback when tab changes */
    onTabChange?: (value: string) => void;
    /** Optional Tailwind height class inserted between the title and tabs (e.g. "h-16") */
    topSpacer?: string;
    /** Optional Tailwind width class to constrain content width (e.g. "w-2/3") */
    contentWidth?: string;
    /** Layout: 'down' stacks title above tabs, 'split' puts title left 50% and tabs right 50% */
    layout?: 'down' | 'split';
    /** Whether to hide the title */
    hideTitle?: boolean;
    /** Optional Tailwind class applied to a wrapper around each tab's content (e.g. "w-[55%] ml-auto") */
    tabContentClassName?: string;
}

export function TabbedSection({
    title,
    tabs,
    defaultTab,
    className,
    onTabChange,
    topSpacer,
    contentWidth,
    layout = 'down',
    tabContentClassName,
}: TabbedSectionProps) {
    const [activeTab, setActiveTab] = React.useState(defaultTab || tabs[0]?.value);
    const sectionTitleClassName = useSectionTitleClassName();

    const handleTabChange = (value: string) => {
        setActiveTab(value);
        onTabChange?.(value);
    };

    const activeTabData = tabs.find(tab => tab.value === activeTab);

    const titleElement = (
        <h3 className={sectionTitleClassName}>{title}</h3>
    );

    const tabsElement = (
        <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div className="flex items-center justify-between">
                <TabsList>
                    {tabs.map((tab) => (
                        <TabsTrigger key={tab.value} value={tab.value}>
                            {tab.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {activeTabData?.tabActions}
            </div>

            {tabs.map((tab) => (
                <TabsContent key={tab.value} value={tab.value}>
                    {tabContentClassName
                        ? <div className={tabContentClassName}>{tab.content}</div>
                        : tab.content}
                </TabsContent>
            ))}
        </Tabs>
    );

    const wrappedTabs = contentWidth
        ? <div className={contentWidth}>{tabsElement}</div>
        : tabsElement;

    if (layout === 'split') {
        return (
            <Card className={cn(PANEL_SPACING.SECTION_BOTTOM_MARGIN, className)}>
                <CardContent className={PANEL_SPACING.CARD_CONTENT_PADDING}>
                    <div className="flex items-start">
                        <div className="w-1/2 shrink-0 pt-1">
                            {titleElement}
                        </div>
                        <div className="w-1/2">
                            {topSpacer && <div className={topSpacer} />}
                            {wrappedTabs}
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={cn(PANEL_SPACING.SECTION_BOTTOM_MARGIN, className)}>
            <CardContent className={PANEL_SPACING.CARD_CONTENT_PADDING}>
                <div className={`flex flex-col ${PANEL_SPACING.SECTION_CONTENT_GAP}`}>
                    {titleElement}
                    {topSpacer && <div className={topSpacer} />}
                    {wrappedTabs}
                </div>
            </CardContent>
        </Card>
    )
}
